import React from 'react';
import { useSelector } from 'react-redux';
import { stripePublishableKey } from 'settings/index';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import LoaderInline from 'components/uielements/LoaderInline';

import { currencyFormatter } from 'utils/currencyHelpers';
import { useGetPartnerStripeInvoiceByIdQuery } from 'lib/Api';
import { ShoppingCartInvoiceInterface } from 'commons/types/shoppingCartTypes';
import { CreateStripeCourseInvoiceResponse } from 'commons/types/paymentTypes';
import { InvoiceInterface } from 'commons/types/stripeTypes';
import CheckoutForm from '../CheckoutForm';
import { FormWrapper } from '../Invoice.styles';

interface InvoiceFormProps {
  // eslint-disable-next-line react/no-unused-prop-types
  partnerId: string;
  // eslint-disable-next-line react/no-unused-prop-types
  invoiceId?: string;
  onSuccess: (() => void) | (() => Promise<void>);
  isNested?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  isFederatedNetwork?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  publisherPartnerId?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  buttonText?: string;
  customErrorHandling?: () => any;
  maxWidth?: string;
  stripeOptions?: StripeElementsOptions;
  customPadding?: string;
}
interface UserPurchaseInvoiceFormProps extends InvoiceFormProps {
  invoice: InvoiceInterface;
  stripeOptions: StripeElementsOptions;
  publisherPartner?: {
    partnerId?: string;
    stripeAccountId?: string;
  };
  onCreateInvoice: (
    stripeCustomerId: string,
  ) => Promise<CreateStripeCourseInvoiceResponse | ShoppingCartInvoiceInterface>;
}

interface InvoiceFormSubComponents {
  UserPurchase: React.FC<UserPurchaseInvoiceFormProps>;
}

const InvoiceForm: React.FC<InvoiceFormProps> & InvoiceFormSubComponents = ({
  buttonText = null,
  customErrorHandling = null,
  customPadding = null,
  invoiceId,
  isFederatedNetwork = false,
  isNested = false,
  maxWidth = null,
  onSuccess,
  partnerId,
  publisherPartnerId = null,
}) => {
  const { data: invoice, isLoading: isInvoiceLoading } = useGetPartnerStripeInvoiceByIdQuery({
    partnerId,
    invoiceId,
    isFederatedNetwork,
    publisherPartnerId,
  });

  const { data: globalPartner, isLoading: isGlobalPartnerLoading } = useSelector(
    (state) => state.partnerInstance.globalPartner,
  );

  if (!invoice || invoice?.paid) {
    return null;
  }

  const stripePromise = loadStripe(stripePublishableKey, {
    stripeAccount: invoice?.stripeAccountId
      ? invoice?.stripeAccountId
      : globalPartner?.parentPartnerStripeAccountId,
  });

  const options: StripeElementsOptions = {
    clientSecret: invoice?.paymentIntent?.clientSecret,
  };

  return isInvoiceLoading || isGlobalPartnerLoading ? (
    <LoaderInline width='9.6rem' />
  ) : (
    <FormWrapper>
      <Elements stripe={stripePromise} options={options}>
        <CheckoutForm
          invoice={invoice}
          onSuccess={onSuccess}
          isNested={isNested}
          buttonText={
            buttonText || `Pay ${currencyFormatter(invoice?.amountDue / 100, invoice?.currency)}`
          }
          customErrorHandling={customErrorHandling}
          maxWidth={maxWidth}
          customPadding={customPadding}
        />
      </Elements>
    </FormWrapper>
  );
};

const UserPurchaseInvoiceForm: React.FC<UserPurchaseInvoiceFormProps> = ({
  customErrorHandling = null,
  customPadding = null,
  invoice,
  isNested = false,
  maxWidth = null,
  stripeOptions = null,
  onSuccess,
  publisherPartner = null,
  onCreateInvoice,
}) => {
  const stripeAccountId = publisherPartner?.stripeAccountId;

  const { data: globalPartner, isLoading: isGlobalPartnerLoading } = useSelector(
    (state) => state.partnerInstance.globalPartner,
  );

  const stripePromise = loadStripe(stripePublishableKey, {
    stripeAccount: stripeAccountId ?? globalPartner?.parentPartnerStripeAccountId,
  });

  return isGlobalPartnerLoading ? (
    <LoaderInline width='9.6rem' />
  ) : (
    <FormWrapper>
      <Elements stripe={stripePromise} options={stripeOptions}>
        <CheckoutForm.UserPurchase
          invoice={invoice}
          onSuccess={onSuccess}
          isNested={isNested}
          buttonText='Place Order'
          customErrorHandling={customErrorHandling}
          maxWidth={maxWidth}
          customPadding={customPadding}
          onCreateInvoice={onCreateInvoice}
          publisherPartner={publisherPartner ?? null}
        />
      </Elements>
    </FormWrapper>
  );
};

InvoiceForm.UserPurchase = UserPurchaseInvoiceForm;

export default InvoiceForm;
